import { SectionCaption } from '@app/core/components/SectionCaption';
import { useReportCtx } from '@app/core/reports/report-ctx';
import { generateShortHash } from '@app/modules/audit/helpers';
import { OrderReprocessAction } from '@app/modules/audit/modules/aod/components/order-reprocess';
import { auditReports } from '@mi-front/api-clients';
import { ChipNavigationProps } from '@mi-front/ui';
import { useMemo } from 'react';
import { defineMessage } from 'react-intl';
import { GipAlternativeReportType } from '../gip-alt-report/preview/types';
import { GipReportType } from '../gip-report/config/form/types';
import { GipReportStatsSummary } from '../gip-report/preview/components/result/GipReportStatsSummary';
import { GipReportChipNavigation } from './GipReportChipNavigation';

const resultsTitleMessage = defineMessage({
  id: 'audit.gip.report.results',
  defaultMessage: 'Resultados',
});

export interface GipReportStatsAndNavigationProps {
  additionalChips?: ChipNavigationProps['chips'];
  defaultActiveChip?: string;
  /** Passed through to {@link GipReportChipNavigation}. */
  useLongLabel?: boolean;
}

export const GipReportStatsAndNavigation = ({
  additionalChips,
  defaultActiveChip = 'with-matches',
  useLongLabel,
}: GipReportStatsAndNavigationProps) => {
  const ctx = useReportCtx<GipReportType | GipAlternativeReportType>();
  const result = ctx.result as auditReports.GipReportResultDto | auditReports.GipAltReportResultDto;
  const values = ctx.values as GipReportType['Values'];

  const reprocessKey = useMemo(
    () =>
      generateShortHash({
        orders: values.orders,
        from: values.from,
        to: values.to,
      }),
    [values.orders, values.from, values.to],
  );

  const navigation = (
    <GipReportChipNavigation
      result={result}
      additionalChips={additionalChips}
      defaultActiveChip={defaultActiveChip}
      useLongLabel={useLongLabel}
      actionComponent={
        result.meta.canReprocess && (
          <OrderReprocessAction
            orders={values.orders}
            reprocessKey={reprocessKey}
            onReprocessComplete={() => {
              ctx.refreshReport();
            }}
          />
        )
      }
    />
  );

  return (
    <>
      <GipReportStatsSummary result={result} />
      <SectionCaption message={resultsTitleMessage} />
      {navigation}
    </>
  );
};
